/**
 * EBIKO BXGY Auto-Add Cart Observer
 * ─────────────────────────────────
 * Watches the cart for products that match an active BXGY rule
 * ("Buy 10 Z250 → free $100 Costco card", "Buy 20 Z250 → free $300 Costco card").
 * When the buy condition is met it auto-adds the matching gift VARIANT to cart
 * with a custom property `_ebk_auto_gift_rule` so we can identify our additions
 * later. When the customer reduces the buy qty below the threshold, the gift
 * auto-removes.
 *
 * Pairs with the Shopify automatic discount (created via the BXGY Tier Wizard
 * → Push) which makes the gift line $0 at checkout. Without auto-add the
 * discount would only fire if the customer manually added the gift card —
 * which they wouldn't, since the badge on the product page reads "get $100
 * Costco card" implying the gift comes free with the purchase.
 *
 * Tier handling: when multiple rules target the same buy product
 * (e.g. Buy 10 → $100 AND Buy 20 → $300), the highest-qty tier whose
 * threshold is met wins. The lower-tier gift gets removed and replaced.
 *
 * Customer override: if the customer manually deletes an auto-added gift,
 * we record the rule_id in sessionStorage with a 30-minute TTL and don't
 * re-add it for that window. After TTL or session end, normal logic resumes.
 */
// build: 2026-05-02-evt-listeners-v3 — forces Shopify to repub asset (was cached on
// pre-schedReconcile hash for ~50 min, content-hash dedup blocked the manual save).
(function () {
  if (window.__ebkBxgyAutoAddInit__) return;
  window.__ebkBxgyAutoAddInit__ = true;

  var API = 'https://ebiko-order-tracker.onrender.com';
  var DEBUG = false;
  function log() { if (DEBUG && window.console) console.log.apply(console, ['[ebk-bxgy]'].concat([].slice.call(arguments))); }

  var rulesByBuyProduct = {};      // pid → [rule, ...]  (sorted desc by buy_qty)
  var rulesById = {};
  var rulesLoadedAt = 0;
  var RULES_TTL_MS = 5 * 60 * 1000;  // re-fetch every 5 min
  var processing = false;
  var pendingRun = false;

  // ─── Failure circuit breaker ──────────────────────────────────
  // If /cart/add.js fails (e.g. Shopify 429 or 422 on an unpurchasable
  // product), keep a per-rule failure count. After N failures within
  // a window, disable auto-add for that rule until the user reloads
  // the page. This is what stops the retry loop that piles up 250+
  // requests when something's broken.
  var ADD_FAIL_LIMIT = 2;            // attempts before giving up on this rule for this session
  var ADD_FAIL_RESET_MS = 60 * 1000; // sliding window for failure count
  var addFailures = {};              // rule_id → [timestamp, ...]
  function noteAddFailure(ruleId) {
    var arr = addFailures[ruleId] || [];
    var cutoff = Date.now() - ADD_FAIL_RESET_MS;
    arr = arr.filter(function (t) { return t > cutoff; });
    arr.push(Date.now());
    addFailures[ruleId] = arr;
  }
  function isAddDisabled(ruleId) {
    var arr = addFailures[ruleId] || [];
    var cutoff = Date.now() - ADD_FAIL_RESET_MS;
    arr = arr.filter(function (t) { return t > cutoff; });
    addFailures[ruleId] = arr;
    return arr.length >= ADD_FAIL_LIMIT;
  }

  // Manual kill switch — paste in console: window.__ebkBxgyAutoAdd.kill()
  var killed = false;

  // ─── User-override memory ─────────────────────────────────────
  // Key by rule_id, value is timestamp of removal. Short TTL — just enough
  // to break the immediate /cart/change.js → reconcile re-add fight loop
  // when customer clicks Remove. After ~1 minute (or any new page session),
  // the auto-add fires again so the gift behaves as advertised on the
  // product page ("Buy 10 → free Costco card") rather than silently missing.
  // Key is suffixed with _v2 to invalidate any existing 30-min flags from
  // the previous version of this script.
  var DISMISS_TTL_MS = 60 * 1000;  // was 30 * 60 * 1000 — see commit notes
  var DISMISS_KEY = 'ebk_bxgy_dismiss_v2';
  function isDismissed(ruleId) {
    try {
      var raw = sessionStorage.getItem(DISMISS_KEY);
      if (!raw) return false;
      var map = JSON.parse(raw);
      var t = map[ruleId];
      if (!t) return false;
      if (Date.now() - t > DISMISS_TTL_MS) {
        delete map[ruleId];
        sessionStorage.setItem(DISMISS_KEY, JSON.stringify(map));
        return false;
      }
      return true;
    } catch (e) { return false; }
  }
  function markDismissed(ruleId) {
    try {
      var raw = sessionStorage.getItem(DISMISS_KEY);
      var map = raw ? JSON.parse(raw) : {};
      map[ruleId] = Date.now();
      sessionStorage.setItem(DISMISS_KEY, JSON.stringify(map));
    } catch (e) {}
  }

  // ─── Rules cache ──────────────────────────────────────────────
  function loadRules() {
    var fresh = (Date.now() - rulesLoadedAt) < RULES_TTL_MS;
    if (fresh && Object.keys(rulesByBuyProduct).length > 0) return Promise.resolve();
    return fetch(API + '/api/storefront/bxgy-rules', { credentials: 'omit' })
      .then(function (r) { return r.json(); })
      .then(function (d) {
        rulesByBuyProduct = {};
        rulesById = {};
        var rules = (d && d.rules) || [];
        rules.forEach(function (rule) {
          if (!rule.gift_variant_id) return; // need a specific variant to add
          rulesById[rule.id] = rule;
          // Multi-product rules: index the same rule under EVERY buy product id
          // so cart-side counting can find it regardless of which product the
          // customer has. The threshold check sums qty across ALL listed
          // products (see reconcile()).
          var pids = (rule.buy_product_ids && rule.buy_product_ids.length > 0)
            ? rule.buy_product_ids.map(String)
            : [String(rule.buy_product_id)];
          // Stash the full pid set on the rule itself for the qty-summing step
          rule._buy_pids = pids;
          pids.forEach(function (pid) {
            if (!rulesByBuyProduct[pid]) rulesByBuyProduct[pid] = [];
            // Avoid pushing the same rule twice (shouldn't happen, but cheap to guard)
            if (rulesByBuyProduct[pid].indexOf(rule) === -1) rulesByBuyProduct[pid].push(rule);
          });
        });
        // Sort each product's tiers by buy_qty DESC so higher tier wins
        Object.keys(rulesByBuyProduct).forEach(function (pid) {
          rulesByBuyProduct[pid].sort(function (a, b) { return b.buy_qty - a.buy_qty; });
        });
        rulesLoadedAt = Date.now();
        log('rules loaded:', rules.length);
      })
      .catch(function (e) { log('rules load failed:', e); });
  }

  // ─── Cart helpers ─────────────────────────────────────────────
  function getCart() {
    return fetch('/cart.js', { credentials: 'same-origin' }).then(function (r) { return r.json(); });
  }

  function addGift(variantId, ruleId) {
    return fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: [{
          id: parseInt(variantId),
          quantity: 1,
          properties: {
            '_ebk_auto_gift_rule': String(ruleId),
            '_ebk_auto_gift': '1'
          }
        }]
      })
    }).then(function (r) {
      if (!r.ok) {
        // 429 → rate-limited; 422 → variant unavailable / inventory / sales-channel
        // restriction; 404 → variant deleted. Note the failure so the circuit breaker
        // can disable retries. Read the response body for Shopify's specific reason —
        // often something like "Variant is not available for sale" or
        // "Cart error: variant cannot be added because it's not in stock"
        noteAddFailure(ruleId);
        return r.text().then(function (body) {
          var reason = body;
          try {
            var parsed = JSON.parse(body);
            reason = (parsed.description || parsed.message || parsed.errors || body);
            if (typeof reason === 'object') reason = JSON.stringify(reason);
          } catch (e) { /* not json — use raw body */ }
          // Always log the 422/etc reason — even when DEBUG is off — because this
          // is the actionable info for the admin to fix the product in Shopify.
          if (window.console && window.console.warn) {
            window.console.warn('[ebk-bxgy] addGift ' + r.status + ' for rule ' + ruleId + ' (variant ' + variantId + '):', reason);
          }
          log('addGift failed:', r.status, 'rule', ruleId, '(', addFailures[ruleId].length, 'failures in window) — reason:', reason);
          var err = new Error('cart/add.js ' + r.status + ': ' + reason);
          err.status = r.status;
          err.body = reason;
          throw err;
        });
      }
      return r.json();
    });
  }

  function removeGiftLine(lineKey) {
    // Use /cart/change.js with line index/key, qty 0
    return fetch('/cart/change.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: lineKey, quantity: 0 })
    }).then(function (r) { return r.json(); });
  }

  // ─── Cart events broadcast (tells the cart drawer to re-render) ───
  function notifyCart(cart) {
    try { document.dispatchEvent(new CustomEvent('cart:refresh', { detail: cart })); } catch (e) {}
    try { document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart })); } catch (e) {}
    try { window.dispatchEvent(new CustomEvent('cart:refresh', { detail: cart })); } catch (e) {}
  }

  // ─── Main reconcile loop ──────────────────────────────────────
  function reconcile() {
    if (killed) return Promise.resolve();
    if (processing) { pendingRun = true; return Promise.resolve(); }
    processing = true;
    return loadRules()
      .then(getCart)
      .then(function (cart) {
        // Index buy-product totals + identify our auto-added gifts
        var qtyByProduct = {};
        var autoGiftLines = []; // [{rule_id, variant_id, line_key, line}]
        for (var i = 0; i < cart.items.length; i++) {
          var item = cart.items[i];
          var pid = String(item.product_id);
          qtyByProduct[pid] = (qtyByProduct[pid] || 0) + (item.quantity || 0);
          var props = item.properties || {};
          var ruleId = props._ebk_auto_gift_rule;
          if (ruleId) {
            autoGiftLines.push({
              rule_id: String(ruleId),
              variant_id: String(item.variant_id),
              line_key: item.key,
              quantity: item.quantity
            });
          }
        }

        // Decide which rule (if any) should be active.
        // For multi-product rules, evaluate ONCE per rule (not once per product
        // it's indexed under) and sum cart qty across all the rule's buy
        // products. Group rules by their canonical "rule group" — the sorted
        // pid list — so multiple tiers covering the same product set compete
        // against each other (highest qty threshold met wins).
        var actions = []; // {action: 'add'|'remove', ...}
        var winningRuleByGroup = {}; // groupKey → rule
        var ruleGroupKey = function (rule) {
          return (rule._buy_pids || [String(rule.buy_product_id)]).slice().sort().join(',');
        };
        var totalQtyForRule = function (rule) {
          var pids = rule._buy_pids || [String(rule.buy_product_id)];
          var sum = 0;
          for (var i = 0; i < pids.length; i++) sum += (qtyByProduct[pids[i]] || 0);
          return sum;
        };

        // De-dupe: a rule indexed under N products would appear N times
        // when iterating rulesByBuyProduct values
        var seenRules = {};
        var allRules = [];
        Object.keys(rulesByBuyProduct).forEach(function (pid) {
          rulesByBuyProduct[pid].forEach(function (r) {
            if (!seenRules[r.id]) { seenRules[r.id] = true; allRules.push(r); }
          });
        });

        // Group rules by their buy-product set so tiers compete
        var rulesByGroup = {};
        allRules.forEach(function (r) {
          var k = ruleGroupKey(r);
          if (!rulesByGroup[k]) rulesByGroup[k] = [];
          rulesByGroup[k].push(r);
        });
        // Sort each group desc by buy_qty so higher tier wins
        Object.keys(rulesByGroup).forEach(function (k) {
          rulesByGroup[k].sort(function (a, b) { return b.buy_qty - a.buy_qty; });
        });

        Object.keys(rulesByGroup).forEach(function (k) {
          var tiers = rulesByGroup[k];
          var groupQty = totalQtyForRule(tiers[0]);  // same pid set — any tier gives the right sum
          var winning = null;
          for (var t = 0; t < tiers.length; t++) {
            if (isAddDisabled(tiers[t].id)) {
              log('skipping rule', tiers[t].id, '— circuit breaker tripped');
              continue;
            }
            if (groupQty >= tiers[t].buy_qty && !isDismissed(tiers[t].id)) {
              winning = tiers[t]; break;
            }
          }
          if (winning) winningRuleByGroup[k] = winning;
        });

        // Reverse map: which rules ARE currently in cart as auto gifts?
        var currentRuleIds = {};
        autoGiftLines.forEach(function (g) { currentRuleIds[g.rule_id] = g; });

        // Add winning rules that aren't yet in cart
        Object.keys(winningRuleByGroup).forEach(function (k) {
          var rule = winningRuleByGroup[k];
          if (!currentRuleIds[String(rule.id)]) {
            actions.push({ action: 'add', rule: rule });
          }
        });

        // Remove auto gifts that aren't winning anymore
        autoGiftLines.forEach(function (g) {
          var rule = rulesById[g.rule_id];
          if (!rule) {
            actions.push({ action: 'remove', line_key: g.line_key, reason: 'rule no longer active' });
            return;
          }
          var winning = winningRuleByGroup[ruleGroupKey(rule)];
          if (!winning || String(winning.id) !== String(rule.id)) {
            actions.push({ action: 'remove', line_key: g.line_key, reason: 'no longer qualifying or superseded' });
          }
        });

        if (actions.length === 0) return cart;

        log('reconcile actions:', actions);

        // Execute actions sequentially to avoid races on Shopify's cart endpoint
        var anyAddSucceeded = false;
        var p = Promise.resolve(cart);
        actions.forEach(function (a) {
          p = p.then(function () {
            if (a.action === 'add') {
              log('adding gift for rule', a.rule.id, '→ variant', a.rule.gift_variant_id);
              return addGift(a.rule.gift_variant_id, a.rule.id)
                .then(function () { anyAddSucceeded = true; })
                .catch(function (e) {
                  log('add failed (rule', a.rule.id, '):', e && e.message);
                  // Don't rethrow — let the chain continue with other actions
                });
            } else if (a.action === 'remove') {
              log('removing line', a.line_key, ':', a.reason);
              return removeGiftLine(a.line_key).catch(function (e) { log('remove failed', e); });
            }
          });
        });
        return p.then(function () {
          // Only re-fetch the cart + dispatch cart:updated when something actually
          // changed in the cart. This prevents the threshold-bar-injector and
          // other listeners from spinning on no-op cart events that we'd
          // otherwise broadcast on every reconcile.
          if (!anyAddSucceeded && actions.every(function (a) { return a.action !== 'remove'; })) {
            return cart;
          }
          return getCart().then(function (updated) {
            notifyCart(updated);
            return updated;
          });
        });
      })
      .catch(function (e) { log('reconcile error:', e); })
      .then(function () {
        processing = false;
        if (pendingRun) {
          pendingRun = false;
          setTimeout(reconcile, 50);
        }
      });
  }

  // ─── Detect manual removal of our auto-gifts (so we don't re-add) ───
  // When the user clicks Remove on an _ebk_auto_gift line, /cart/change.js or
  // /cart/update.js fires with quantity 0 for that variant. We can't see
  // properties from the URL/payload alone, but we capture the cart state
  // BEFORE the change in our fetch hook and compare AFTER.
  var lastAutoGiftKeys = new Set ? new Set() : null;
  function rememberAutoGifts(cart) {
    if (!lastAutoGiftKeys) return;
    lastAutoGiftKeys = new Set();
    (cart.items || []).forEach(function (it) {
      if (it.properties && it.properties._ebk_auto_gift_rule) {
        lastAutoGiftKeys.add(String(it.key) + '|' + String(it.properties._ebk_auto_gift_rule));
      }
    });
  }
  function detectManualRemoval(cart) {
    if (!lastAutoGiftKeys) return;
    var stillThere = new Set();
    (cart.items || []).forEach(function (it) {
      if (it.properties && it.properties._ebk_auto_gift_rule) {
        stillThere.add(String(it.key) + '|' + String(it.properties._ebk_auto_gift_rule));
      }
    });
    lastAutoGiftKeys.forEach(function (token) {
      if (!stillThere.has(token)) {
        var ruleId = token.split('|')[1];
        if (ruleId) {
          log('manual removal detected — dismissing rule', ruleId, 'for 30 min');
          markDismissed(ruleId);
        }
      }
    });
    lastAutoGiftKeys = stillThere;
  }

  // ─── Hook fetch to react to cart-update endpoints ─────────────
  var origFetch = window.fetch;
  window.fetch = function () {
    var args = arguments;
    var url = '';
    try { url = (args[0] && args[0].url) ? args[0].url : (args[0] || '').toString(); } catch (e) {}
    var isCartMut = /\/cart\/(add|change|update|clear)\.js/.test(url);
    var isCartGet = /\/cart\.js(\?|$)/.test(url);

    return origFetch.apply(this, args).then(function (resp) {
      if (killed) return resp;
      if (isCartMut || isCartGet) {
        // Clone response body to read it without consuming for the caller
        try {
          resp.clone().json().then(function (data) {
            // /cart/add.js returns the added item, others return the cart
            if (data && data.items) {
              detectManualRemoval(data);
              rememberAutoGifts(data);
            }
          }).catch(function () {});
        } catch (e) {}
        if (isCartMut && resp.ok) {
          // Only schedule reconcile after a SUCCESSFUL cart mutation. If the
          // mutation 4xx'd (e.g. our own /cart/add.js failed with 429), the
          // cart didn't change — reconciling would just re-trigger the same
          // failed add. The circuit breaker ALSO catches this, but skipping
          // here is cheaper.
          setTimeout(reconcile, 250);
        }
      }
      return resp;
    });
  };

  // ─── Boot: load rules + initial reconcile ─────────────────────
  function boot() {
    return loadRules().then(getCart).then(function (cart) {
      rememberAutoGifts(cart);
      return reconcile();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Re-run on every cart-related event the theme might dispatch. The theme.js
  // (line ~11366-11368) fires cart:updated / cart:refresh / cart:change after
  // qty changes, removals, and adds. Without these listeners the auto-add
  // only ran via the fetch monkey-patch, which can race against page-load
  // timing or other scripts that wrap fetch first — symptom: user adds
  // qty 10, Costco card doesn't appear until they refresh the page.
  // Debounced 200ms so multiple events for the same cart change collapse.
  var _evtDebounce = null;
  function schedReconcile(reason) {
    clearTimeout(_evtDebounce);
    _evtDebounce = setTimeout(function () {
      log('event-triggered reconcile:', reason);
      reconcile();
    }, 200);
  }
  [
    'cart:refresh', 'cart:update', 'cart:updated', 'cart:change',
    'cart:item-added', 'cart:item-removed', 'cart:item-updated',
    'cart:rerendered', 'cart:rerender', 'cart:build',
    'cart-drawer:open', 'cart:open'
  ].forEach(function (evt) {
    document.addEventListener(evt, function () { schedReconcile(evt); });
  });
  // Theme also fires shopify:section:load when the mini-cart re-renders
  document.addEventListener('shopify:section:load', function (e) {
    if (e.target && e.target.id && (e.target.id.indexOf('shopify-section-mini-cart') === 0 || e.target.id.indexOf('shopify-section-main-cart') === 0)) {
      schedReconcile('shopify:section:load');
    }
  });

  // Expose for debugging + emergency disable
  window.__ebkBxgyAutoAdd = {
    reconcile: reconcile,
    loadRules: function () { rulesLoadedAt = 0; return loadRules(); },
    getRules: function () { return Object.keys(rulesById).map(function (k) { return rulesById[k]; }); },
    debug: function (v) { DEBUG = v !== false; },
    kill: function () { killed = true; addFailures = {}; log('observer KILLED for this session — reload to re-enable'); },
    failures: function () { return JSON.parse(JSON.stringify(addFailures)); }
  };
})();
